export type OtpSendOutcome = {
  ok: boolean;
  message: string;
  retryable: boolean;
  cooldownSeconds: number | null;
  devOtp: string | null;
};

const RETRYABLE_CODES = new Set([
  'email_failed',
  'email_rate_limited',
  'email_temporarily_unavailable',
  'email_smtp_unavailable',
  'email_auth_failed',
  'email_quota_exceeded',
  'provider_not_configured',
]);

/**
 * A send is successful only when the HTTP response and the API payload both
 * confirm the mail operation was accepted. Clicking Get Code is not success.
 */
export function interpretOtpSendResponse(status: number, data: unknown): OtpSendOutcome {
  const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const successFlag = payload.success === true && payload.status !== false;
  const message = typeof payload.message === 'string' && payload.message.trim() !== ''
    ? payload.message.trim()
    : '';
  const errorCode = typeof payload.error_code === 'string' ? payload.error_code : '';
  const rawCooldown = payload.cooldown_seconds ?? (payload.data as { cooldown_seconds?: unknown } | undefined)?.cooldown_seconds;
  const cooldownSeconds = typeof rawCooldown === 'number' && rawCooldown > 0 ? rawCooldown : null;
  const devOtp = typeof payload.otp === 'string' && /^\d{6}$/.test(payload.otp) ? payload.otp : null;

  if (status >= 200 && status < 300 && successFlag) {
    return {
      ok: true,
      message: message || 'Verification code sent to your email.',
      retryable: false,
      cooldownSeconds,
      devOtp,
    };
  }

  const retryable = status === 502 || status === 503 || status === 504 || RETRYABLE_CODES.has(errorCode);

  return {
    ok: false,
    message: message || (status === 503
      ? 'Email could not be sent. Please try again shortly.'
      : 'Failed to send verification code.'),
    retryable,
    cooldownSeconds,
    devOtp: null,
  };
}

export function shouldRefreshSessionAfter401(rawUrl: string): boolean {
  const path = normalizeApiPath(rawUrl);
  if (path === '/auth/me' || path === '/admin/me') return false;
  if (
    path === '/auth/login' ||
    path === '/auth/refresh' ||
    path === '/admin/login' ||
    path === '/admin/refresh'
  ) {
    return false;
  }
  if (
    path.startsWith('/auth/') ||
    path.startsWith('/public/') ||
    path === '/register' ||
    path.startsWith('/register/') ||
    path === '/signup' ||
    path.startsWith('/signup/')
  ) {
    return false;
  }
  return true;
}

function normalizeApiPath(rawUrl: string): string {
  const raw = String(rawUrl || '').split('?')[0];
  try {
    if (/^https?:\/\//i.test(raw)) {
      const pathname = new URL(raw).pathname.replace(/\/+$/, '') || '/';
      return pathname.replace(/^\/api(?=\/)/, '') || '/';
    }
  } catch {
    /* ignore */
  }
  return raw.replace(/^\/+/, '/').replace(/\/+$/, '') || '/';
}
